import React, { useMemo } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { DropdownMenuTypePropType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenuTitleWithLabel } from 'ui-components/components/dropdown-menu-title-with-label';
import { MDash } from 'ui-components/components/mdash/mdash';
import { testId } from 'ui-components/services/testid-service';

import ShipFromDropdown from './ship-from-dropdown';
import MissingShipFrom from '../../components/shipment-configuration-widget/missing-ship-from';
import { PENDING_FULFILLMENT } from '../../services/api/order-status-from-view-service';
import { DEFAULTS } from '../../ship-from/components/ship-from-dropdown/constants';
import { ValidationPropType } from '../../ship-from/components/ship-from-dropdown/prop-types';
import Global from '../../store/global-selectors';
import { useFulfillmentPlannerContext } from '../context';
import getMarketplaceDisplayName from '../../components/data-driven-logo/get-marketplace-display-name';
import { ShipFromSearchableDropdown } from './ship-from-searchable-dropdown';
import { useEnableShipFromSearchableDropdown } from '../../feature-flags/hooks/use-enable-ship-from-searchable-dropdown';

import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';

import style from './style.module.less';
import message from '../messages';

const ShipFromDropdownRenderer = ({
  id,
  dropdownMenuType,
  onChange,
  parentComponentName = '',
  selectedShipFromId,
  shipFroms,
  validation = DEFAULTS.validation,
  onAddShipFromSuccess,
  singleItemTitleClassName,
}) => {
  const { fulfillmentPlanId } = useFulfillmentPlannerContext();
  const orderGridStatus =
    useSelector((state) =>
      Global.selectEntitiesFulfillmentPlanOrderGridStatus(state, {
        fulfillmentPlanId,
      }),
    ) ?? '';
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const firstSalesOrder = useSelector((state) =>
    Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, {
      fulfillmentPlanIds: [fulfillmentPlanId],
    }),
  )[0];

  const intl = useIntl();
  const shouldUseSearchableDropdown = useEnableShipFromSearchableDropdown();

  const memoizedValidation = useMemo(() => {
    if (fulfillmentPlan?.marketplaceData?.requestedShipFrom) {
      let shipFromName;

      // Try to parse as JSON first (for backwards compatibility)
      try {
        const shipFromData = JSON.parse(fulfillmentPlan.marketplaceData.requestedShipFrom);
        shipFromName = shipFromData?.name;
      } catch {
        // If parsing fails, treat it as a plain string
        shipFromName = fulfillmentPlan.marketplaceData.requestedShipFrom;
      }

      const marketplaceName = getMarketplaceDisplayName(firstSalesOrder.store.marketplaceId).defaultMessage;

      if (marketplaceName && shipFromName) {
        return {
          helperText: intl.formatMessage(message.requestedShipFrom, {
            marketplaceName,
            shipFromName,
          }),
          helperTextType: INLINE_MESSAGE_TYPES.warning,
        };
      }
    }

    return validation;
  }, [fulfillmentPlan, firstSalesOrder, validation, intl]);

  if (orderGridStatus === PENDING_FULFILLMENT) {
    return <MDash className={style.mdash} />;
  }

  if (shipFroms.length === 0) {
    return <MissingShipFrom onAddShipFromSuccess={onAddShipFromSuccess} triggerSource={`CSW-${parentComponentName}`} />;
  }

  if (shipFroms.length === 1) {
    const [oneAndOnly] = shipFroms;
    const { helperText, helperTextType } = memoizedValidation;

    return (
      <DropdownMenuTitleWithLabel
        {...testId('csw-ship-from')}
        className={style['dropdown-menu-single']}
        classNameTitle={classnames(style['dropdown-menu-single-item'], singleItemTitleClassName)}
        inlineMessage={helperText}
        inlineMessageType={helperTextType}
        title={oneAndOnly.name}
      />
    );
  }

  if (shouldUseSearchableDropdown) {
    return (
      <ShipFromSearchableDropdown
        id={id}
        onChange={onChange}
        selectedShipFromId={selectedShipFromId}
        shipFroms={shipFroms}
        validation={memoizedValidation}
      />
    );
  }

  return (
    <ShipFromDropdown
      dropdownMenuType={dropdownMenuType}
      id={id}
      onChange={onChange}
      selectedShipFromId={selectedShipFromId}
      shipFroms={shipFroms}
      validation={memoizedValidation}
    />
  );
};

ShipFromDropdownRenderer.propTypes = {
  id: PropTypes.string,
  dropdownMenuType: DropdownMenuTypePropType,
  onChange: PropTypes.func.isRequired,
  selectedShipFromId: PropTypes.string.isRequired,
  shipFroms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  validation: ValidationPropType,
  onAddShipFromSuccess: PropTypes.func.isRequired,
  parentComponentName: PropTypes.string,
  singleItemTitleClassName: PropTypes.string,
};

export default ShipFromDropdownRenderer;
